/**
 * **Detail produk** — identitas struk · kontrol · ketersediaan berjadwal · barcode.
 *
 * ### ⛔ Kenapa BUKAN satu formulir 40 radio ala VTEC
 * Form produk VTEC menumpuk empat puluhan `yes/no` dalam satu halaman — dan itu sumber
 * penyakitnya: opsi bertambah sampai tak ada yang berani mematikan satu pun, karena tak ada yang
 * ingat apa akibatnya. Di sini ia dipecah menurut **akibat**: yang mengubah tampilan, yang
 * MEMAKSA, yang menjadwalkan, dan yang dipindai.
 *
 * ### ⭐ Yang ditegakkan di sini bukan kosmetik
 * - `discount_allowed` — tanpa ini rokok & minuman botol bisa didiskon diam-diam; ia tak pernah
 *   muncul sebagai galat, hanya sebagai margin yang menipis tanpa sebab yang bisa ditunjuk.
 * - `receipt_name` — kertas struk 32–42 karakter; yang terpotong biasanya justru pembedanya
 *   ("… Pedas" vs "… Tidak Pedas").
 * - ketersediaan **kosong = selalu tersedia**, bukan `00:00–23:59`: yang kedua membuat pertanyaan
 *   "produk ini memang berjadwal?" tak bisa dijawab tanpa menebak maksud angkanya.
 */
import {
  Alert, Button, Card, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space,
  Switch,
  TimePicker, Typography, message,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Product, TaxGroup } from '../api/client';
import { canWrite } from '../api/session';
import { ProductCombo } from '../components/ProductCombo';
import { ProductModifiers } from '../components/ProductModifiers';

/** ISO 1=Senin … 7=Minggu — sama dengan `available_days` di DB, tanpa penerjemahan angka. */
const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export interface ProductDetailProps {
  readonly product: Product;
  /** ⚠️ Menerima produk yang SUDAH tersimpan — layar di atasnya memakai nama barunya, bukan yang lama. */
  readonly onSaved: (tersimpan: Product) => void;
}

export function ProductDetail({ product, onSaved }: ProductDetailProps) {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  /**
   * ⭐⭐ **Kelas fiskal bisa DIPINDAH di sini** — sebelumnya hanya bisa disetel saat produk LAHIR.
   *
   * ⛔ Akibat lubang itu nyata: produk yang terlanjur dibuat dengan grup yang salah tak punya jalan
   * perbaikan sama sekali lewat portal, dan menghapusnya pun mustahil begitu ia pernah terjual
   * (`referencedBy` → `order_line`). Jadi ia terkunci di kelas fiskal yang salah — rokok kena
   * service charge selamanya — sampai ada yang menyentuh SQL.
   *
   * ⚠️ Server sudah membolehkannya sejak awal (`products.update.taxGroupId`); yang belum ada
   * layarnya. Setelan yang bisa dibuat tapi tak bisa diperbaiki lebih buruk daripada tak ada.
   */
  const [grupPajak, setGrupPajak] = useState<readonly TaxGroup[]>([]);
  useEffect(() => { void api.taxGroups().then(setGrupPajak).catch(() => undefined); }, []);

  useEffect(() => {
    form.setFieldsValue({
      name: product.name,
      taxGroupId: product.tax_group_id,
      isActive: product.is_active !== false,
      receiptName: product.receipt_name ?? '',
      description: product.description ?? '',
      sortOrder: product.sort_order ?? 0,
      discountAllowed: product.discount_allowed !== false,
      zeroPriceAllowed: product.zero_price_allowed === true,
      returnable: product.returnable !== false,
      dateRange:
        product.available_from == null && product.available_to == null
          ? null
          : [
              product.available_from == null ? null : dayjs(product.available_from),
              product.available_to == null ? null : dayjs(product.available_to),
            ],
      timeRange:
        product.available_time_from == null && product.available_time_to == null
          ? null
          : [
              dayjs(product.available_time_from ?? '00:00', 'HH:mm:ss'),
              dayjs(product.available_time_to ?? '23:59', 'HH:mm:ss'),
            ],
      days: product.available_days ?? [],
    });
  }, [form, product]);

  const simpan = async (v: Record<string, unknown>) => {
    setBusy(true);
    try {
      const tanggal = v['dateRange'] as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
      const jam = v['timeRange'] as [dayjs.Dayjs, dayjs.Dayjs] | null;
      const hari = (v['days'] as number[] | undefined) ?? [];
      const tersimpan = await api.updateProduct(product.id, {
        name: v['name'],
        /*
         * ⚠️ Berlaku untuk penjualan BERIKUTNYA saja. Struk yang sudah keluar membawa pajaknya
         * sendiri di `order_line` (K11: pajak = BARIS), dan itu memang tak boleh berubah surut —
         * mengubahnya berarti angka yang sudah diberikan ke tamu tak lagi bisa dijelaskan.
         */
        taxGroupId: v['taxGroupId'],
        receiptName: (v['receiptName'] as string) === '' ? null : v['receiptName'],
        description: (v['description'] as string) === '' ? null : v['description'],
        sortOrder: v['sortOrder'],
        discountAllowed: v['discountAllowed'],
        zeroPriceAllowed: v['zeroPriceAllowed'],
        returnable: v['returnable'],
        /*
         * ⚠️ Kosong dikirim sebagai `null`, BUKAN sebagai rentang penuh. "Selalu tersedia" dan
         * "dijadwalkan 00:00–23:59" adalah dua pernyataan berbeda, dan yang kedua tak bisa
         * dibedakan dari salah setel.
         */
        availableFrom: tanggal?.[0]?.format('YYYY-MM-DD') ?? null,
        availableTo: tanggal?.[1]?.format('YYYY-MM-DD') ?? null,
        availableTimeFrom: jam?.[0]?.format('HH:mm') ?? null,
        availableTimeTo: jam?.[1]?.format('HH:mm') ?? null,
        availableDays: hari.length === 0 ? null : [...hari].sort((a, b) => a - b),
      });
      /* ⚠️ Status punya endpointnya sendiri, dan hanya disentuh bila memang berubah — jejak master
         yang penuh peristiwa "diubah" tanpa perubahan tak bisa dibaca lagi saat dibutuhkan. */
      if (v['isActive'] !== (product.is_active !== false)) {
        await api.setStatus('products', product.id, v['isActive'] === true);
      }
      void message.success(t('product.saved'));
      onSaved({ ...tersimpan, is_active: v['isActive'] === true });
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card size="small" title={`${t('product.detail')} · ${product.code}`} data-testid="produk-detail">
      <Form form={form} layout="vertical" disabled={!boleh} onFinish={(v) => void simpan(v)}>
        <Row gutter={16}>
          {/* ── identitas yang tercetak ─────────────────────────────────── */}
          <Col span={8}>
            {/* ⭐ Nama disunting DI SINI, bukan di laci: produk punya terlalu banyak isi untuk satu
                laci sempit, dan namanya justru yang paling sering diperbaiki. */}
            <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
              <Input maxLength={120} data-testid="produk-nama" />
            </Form.Item>
            <Form.Item name="receiptName" label={t('product.receiptName')} tooltip={t('product.receiptHint')}>
              <Input maxLength={60} data-testid="produk-receipt-name" />
            </Form.Item>
            <Form.Item name="sortOrder" label={t('product.sortOrder')}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="description" label={t('product.description')}>
              <Input.TextArea rows={2} maxLength={500} />
            </Form.Item>
            {/*
              ⭐⭐ **Kelas fiskal** — dan ia ditaruh di kolom identitas, bukan di antara sakelar
              kontrol, karena ia menjawab "barang ini apa", bukan "boleh diapakan".

              ⚠️ Peringatan di bawahnya bukan hiasan: mengubahnya TIDAK menyentuh struk yang sudah
              keluar. Pajak melekat pada BARIS pesanan (K11), disimpan saat penjualan terjadi — dan
              memang harus begitu, kalau tidak angka yang sudah diberikan ke tamu berubah sendiri
              tiap kali master disunting.
            */}
            <Form.Item
              name="taxGroupId"
              label={t('tax.group')}
              rules={[{ required: true }]}
              tooltip={t('tax.groupHint')}
              extra={t('product.taxGroupForward')}
            >
              <Select
                data-testid="produk-kelas-fiskal"
                options={grupPajak.map((g) => ({ value: g.id, label: `${g.code} · ${g.name}` }))}
              />
            </Form.Item>
          </Col>

          {/* ── kontrol yang MEMAKSA ────────────────────────────────────── */}
          <Col span={8}>
            <Typography.Text strong>{t('product.control')}</Typography.Text>
            <Alert type="warning" showIcon style={{ margin: '8px 0' }} title={t('product.controlHint')} />
            <Space orientation="vertical">
              <Form.Item name="discountAllowed" valuePropName="checked" noStyle>
                <Switch data-testid="flag-diskon" checkedChildren={t('product.discountAllowed')}
                        unCheckedChildren={t('product.discountAllowed')} />
              </Form.Item>
              <Form.Item name="zeroPriceAllowed" valuePropName="checked" noStyle>
                <Switch data-testid="flag-nol" checkedChildren={t('product.zeroPriceAllowed')}
                        unCheckedChildren={t('product.zeroPriceAllowed')} />
              </Form.Item>
              <Form.Item name="returnable" valuePropName="checked" noStyle>
                <Switch data-testid="flag-retur" checkedChildren={t('product.returnable')}
                        unCheckedChildren={t('product.returnable')} />
              </Form.Item>
              {/* ⚠️ Nonaktif = mati di SELURUH outlet brand. Per outlet ada di layar Ketersediaan. */}
              <Form.Item name="isActive" valuePropName="checked" noStyle>
                <Switch data-testid="flag-aktif" checkedChildren={t('outlets.active')}
                        unCheckedChildren={t('outlets.inactive')} />
              </Form.Item>
            </Space>
          </Col>

          {/* ── ketersediaan berjadwal ──────────────────────────────────── */}
          <Col span={8}>
            <Typography.Text strong>{t('product.availability')}</Typography.Text>
            <Alert type="info" showIcon style={{ margin: '8px 0' }} title={t('product.alwaysHint')} />
            <Form.Item name="dateRange" label={t('product.dateRange')}>
              <DatePicker.RangePicker style={{ width: '100%' }} allowEmpty={[true, true]} />
            </Form.Item>
            <Form.Item name="timeRange" label={t('product.timeRange')}>
              <TimePicker.RangePicker style={{ width: '100%' }} format="HH:mm" />
            </Form.Item>
            <Form.Item name="days" label={t('product.days')}>
              <Checkbox.Group data-testid="produk-hari">
                {DAYS.map((d) => (
                  <Checkbox key={d} value={d} data-testid={`hari-${String(d)}`}>{d}</Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Col>
        </Row>

        {boleh && (
          <Button type="primary" htmlType="submit" loading={busy} data-testid="produk-simpan">
            {t('product.save')}
          </Button>
        )}
      </Form>

      {/*
        ⭐ Modifier DI BAWAH form, bukan di dalamnya: memasang grup berlaku SEKETIKA (tabel kaitan),
        sementara isi form baru tersimpan saat tombol ditekan. Menyatukannya membuat satu layar
        punya dua makna "tersimpan" — dan yang menekan Simpan akan mengira modifiernya ikut batal
        kalau ia menekan Batal.
      */}
      <ProductModifiers productId={product.id} />

      {/*
        ⭐⭐ Susunan combo muncul HANYA untuk produk ber-jenis `combo` — bukan kartu kosong di tiap
        produk biasa. ⚠️ Jenis produk tak bisa diubah sesudah lahir (ia ikut tercatat di pesanan
        yang lewat), jadi kartunya tak akan muncul/hilang di tengah penyuntingan.
      */}
      {product.product_kind === 'combo' && <ProductCombo productId={product.id} />}
    </Card>
  );
}
